import { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";

import "../css/PopupInvoicePrinting.css";

import ButtonIconH from "../../components/ButtonIconH";
import CheckBox from "../../components/CheckBox";
import LoadingScreen from "../../components/LoadingScreen";
import { post } from "../js/api";
import { DEBUG } from "../js/debug";
import { userData, selectedOptic } from "../../js/utils";

export const INVOICE_TYPE_TO_TIPO_FACTURA = {
  "imp-ticket": "simple",
  "imp-factura-canje": "simple-canje",
  "imp-ticket-abono": "simple-abono",
  "imp-ticket-rect": "simple-rectificativa",
  "imp-factura": "completa",
  "imp-factura-abono": "completa-abono",
  "imp-factura-rect": "completa-rectificativa",
};

const SIMPLE_TIPOS_FACTURA = [
  "simple",
  "simple-abono",
  "simple-rectificativa",
];

export const openDataUriInNewTab = async (dataUri, fileName) => {
  const [header, base64Data] = dataUri.split(",");
  const mimeType = header.split(":")[1].split(";")[0];
  const byteString = atob(base64Data);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }

  let fileBytes = bytes;
  if (fileName && mimeType === "application/pdf") {
    // El nombre del blob es siempre un UUID aleatorio; el visor de PDF de
    // Chrome usa el título del propio documento (metadato /Title) como
    // nombre visible y como nombre por defecto al guardar.
    try {
      const pdfDoc = await PDFDocument.load(bytes);
      pdfDoc.setTitle(fileName);
      fileBytes = await pdfDoc.save();
    } catch {
      fileBytes = bytes;
    }
  }

  const blobUrl = URL.createObjectURL(
    fileName
      ? new File([fileBytes], fileName, { type: mimeType })
      : new Blob([fileBytes], { type: mimeType }),
  );
  window.open(blobUrl, "_blank");
};

const postTicket = async (conexionTicket, body) => {
  const endpoint = conexionTicket.replace(/\/+$/, "") + "/ImprimirTicket";
  try {
    const response = await fetch(endpoint, {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const textData = await response.text();
    let data;
    try {
      data = JSON.parse(textData);
    } catch {
      data = textData;
    }
    if (DEBUG) {
      const debug = {
        endpoint,
        body,
        responseText: textData,
        responseJSON: data,
      };
      console.log(debug);
    }
    return data;
  } catch (e) {
    if (DEBUG) {
      const debug = {
        endpoint,
        body,
      };
      console.log(debug);
    }
    console.error(e, endpoint);
    return null;
  }
};

const TICKET_SERVICE_TIMEOUT_MS = 500;

export const isTicketServiceOpen = async (conexionTicket) => {
  if (!conexionTicket) {
    if (DEBUG) {
      console.log({ endpoint: "/alive", error: "conexionTicket vacío" });
    }
    return false;
  }

  // El servicio de tickets expone /alive, que responde { status: "ok" } si
  // está levantado. En cualquier otro caso (puerto cerrado, sin respuesta a
  // tiempo, respuesta inesperada) se trata como no disponible y se imprime en
  // A4. Si luego la impresora falla, ImprimirTicket devuelve Success: false y
  // también se genera el A4.
  const endpoint = conexionTicket.replace(/\/+$/, "") + "/alive";
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    TICKET_SERVICE_TIMEOUT_MS,
  );
  try {
    const response = await fetch(endpoint, { signal: controller.signal });
    const textData = await response.text();
    let data;
    try {
      data = JSON.parse(textData);
    } catch (parseError) {
      if (DEBUG) {
        console.error(parseError.message, textData);
      } else {
        console.error(parseError.message);
      }
      throw parseError;
    }
    if (DEBUG) {
      const debug = {
        endpoint,
        responseText: textData,
        responseJSON: data,
      };
      console.log(debug);
    }
    return response.ok && data?.status === "ok";
  } catch (e) {
    if (DEBUG) {
      const debug = {
        endpoint,
      };
      console.log(debug);
    }
    console.error(e, endpoint);
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

// Genera siempre el PDF de la factura (para abrirlo o adjuntarlo a un email).
export const printInvoice = async ({
  idVenta,
  invoiceType,
  printObservations = false,
  printPayments = false,
  createPopupQuestion,
  removePopupQuestion,
}) => {
  const response = await post("gestionDocumental/ImprimirFactura.php", {
    token: userData.token,
    idOptica: selectedOptic.id,
    idVenta,
    tipoFactura: INVOICE_TYPE_TO_TIPO_FACTURA[invoiceType] ?? "",
    imprimirObservaciones: printObservations,
    imprimirPagos: printPayments,
  });

  if (!response || response.error || !response.fichero) {
    createPopupQuestion(
      response?.error || "Error al generar factura.",
      removePopupQuestion,
      null,
      "Cerrar",
    );
    return null;
  }

  return response;
};

// Envía el documento a la impresora de tickets y, si la óptica lo tiene
// configurado, ofrece imprimir una segunda copia. Devuelve false si no se ha
// podido imprimir (error de red o Success: false), para que quien llama
// genere el A4 en su lugar.
export const sendTicket = async ({
  conexionTicket,
  body,
  dobleCopia,
  createPopupQuestion,
  removePopupQuestion,
}) => {
  const response = await postTicket(conexionTicket, body);
  if (response === null || response.Success === false) return false;

  if (dobleCopia) {
    createPopupQuestion(
      "Pulse Aceptar para imprimir el segundo ticket.",
      async () => {
        removePopupQuestion();
        await postTicket(conexionTicket, body);
      },
      null,
      "Aceptar",
    );
  }
  return true;
};

const printTicket = async ({
  idVenta,
  tipoFactura,
  config,
  createPopupQuestion,
  removePopupQuestion,
}) => {
  const bodyFactura = await post("gestionDocumental/ObtenerBodyFactura.php", {
    token: userData.token,
    idOptica: selectedOptic.id,
    idVenta,
    tipoFactura,
  });

  return await sendTicket({
    conexionTicket: config.conexionTicket,
    body: bodyFactura,
    dobleCopia: config.confTicketDobleCopiaVenta,
    createPopupQuestion,
    removePopupQuestion,
  });
};

// Imprime la factura: los tickets simples van al servicio de tickets si está
// levantado; en cualquier otro caso se abre el PDF.
export const printInvoiceDirect = async (params) => {
  const tipoFactura = INVOICE_TYPE_TO_TIPO_FACTURA[params.invoiceType] ?? "";

  if (SIMPLE_TIPOS_FACTURA.includes(tipoFactura)) {
    // Las pantallas que no tienen la configuración de la óptica cargada no
    // la pasan, así que se consulta aquí.
    let config = params.opticConfig;
    if (!config) {
      config = await post("ventas/ObtenerConfiguracionOpticaVenta.php", {
        token: userData.token,
        idOptica: selectedOptic.id,
      });
      config = {
        ...config,
        ticketServiceOpen: await isTicketServiceOpen(config?.conexionTicket),
      };
    }

    // Si la impresora de tickets falla, se genera el A4
    if (
      config?.ticketServiceOpen &&
      (await printTicket({ ...params, tipoFactura, config }))
    ) {
      return true;
    }
  }

  const response = await printInvoice(params);
  if (!response) return false;

  await openDataUriInNewTab(response.fichero, response.nombreFichero);
  return true;
};

export default ({
  show,
  title = "Imprimir factura",
  buttonText = "Imprimir",
  idVenta,
  invoiceType,
  opticConfig,
  onPrint,
  onClose,
  createPopupQuestion,
  removePopupQuestion,
}) => {
  const [printObservations, setPrintObservations] = useState(false);
  const [printPayments, setPrintPayments] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (show) {
      setPrintObservations(false);
      setPrintPayments(false);
    }
  }, [show]);

  const printInvoice = async () => {
    setPrinting(true);
    const success = onPrint
      ? await onPrint({ printObservations, printPayments })
      : await printInvoiceDirect({
          idVenta,
          invoiceType,
          printObservations,
          printPayments,
          opticConfig,
          createPopupQuestion,
          removePopupQuestion,
        });
    setPrinting(false);
    if (success) {
      onClose();
    }
  };

  return (
    show && (
      <div className="popup-container popup-normal popup-invoice-printing">
        <div className="popup-message">
          <div className="popup-header">
            <div className="popup-title">{title}</div>
            <img
              src="/images/close-white.svg"
              loading="lazy"
              alt=""
              className="popup-close"
              onClick={onClose}
            />
          </div>
          <div className="popup-body">
            {printing && <LoadingScreen message="" />}
            <div className="popup-row">
              <div
                className="data-field c-pointer"
                onClick={() => setPrintObservations((prev) => !prev)}
              >
                <CheckBox active={printObservations} onClick={() => {}} />
                <span className="label">Imprimir observaciones</span>
              </div>
              <div
                className="data-field c-pointer"
                onClick={() => setPrintPayments((prev) => !prev)}
              >
                <CheckBox active={printPayments} onClick={() => {}} />
                <span className="label">Imprimir pagos</span>
              </div>
            </div>
          </div>
          <div className="popup-footer">
            <div className="popup-row-buttons">
              <ButtonIconH
                text={buttonText}
                disabled={printing}
                onClick={printInvoice}
              />
            </div>
          </div>
        </div>
      </div>
    )
  );
};
